import { ESdChangeReason, IKuikaStyle, InvisibleStylePart, Visibility } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import StyleLabel from "./style-label/style-label";
import { styleSectionNeedsRender } from "./styling-window-helpers";

interface TabOption {
  value: string;
  label?: string;
  icon?: React.ReactNode;
}

const POINTER_EVENTS_OPTIONS: TabOption[] = [
  { value: "auto", label: "Auto" },
  { value: "none", label: "None" }
];

const BLENDING_OPTIONS: KuikaSelectboxOption[] = [
  { value: "normal", label: "Normal" },
  { value: "multiply", label: "Multiply" },
  { value: "screen", label: "Screen" },
  { value: "overlay", label: "Overlay" },
  { value: "darken", label: "Darken" },
  { value: "lighten", label: "Lighten" },
  { value: "color-dodge", label: "Color Dodge" },
  { value: "color-burn", label: "Color Burn" },
  { value: "hard-light", label: "Hard Light" },
  { value: "soft-light", label: "Soft Light" },
  { value: "difference", label: "Difference" },
  { value: "exclusion", label: "Exclusion" },
  { value: "hue", label: "Hue" },
  { value: "saturation", label: "Saturation" },
  { value: "color", label: "Color" },
  { value: "luminosity", label: "Luminosity" }
];

const CURSOR_GROUP_DEFINITIONS: KuikaSelectboxOptionGroup[] = [
  {
    label: "General",
    options: [
      { value: "auto", label: "Auto" },
      { value: "default", label: "Default" },
      { value: "none", label: "None" }
    ]
  },
  {
    label: "Link & Status",
    options: [
      { value: "pointer", label: "Pointer" },
      { value: "help", label: "Help" },
      { value: "wait", label: "Wait" },
      { value: "progress", label: "Progress" },
      { value: "not-allowed", label: "Not Allowed" }
    ]
  },
  {
    label: "Selection",
    options: [
      { value: "text", label: "Text" },
      { value: "vertical-text", label: "Vertical Text" },
      { value: "cell", label: "Cell" },
      { value: "crosshair", label: "Crosshair" }
    ]
  },
  {
    label: "Drag & Drop",
    options: [
      { value: "grab", label: "Grab" },
      { value: "grabbing", label: "Grabbing" },
      { value: "move", label: "Move" },
      { value: "all-scroll", label: "All Scroll" }
    ]
  },
  {
    label: "Zoom",
    options: [
      { value: "zoom-in", label: "Zoom In" },
      { value: "zoom-out", label: "Zoom Out" }
    ]
  },
  {
    label: "Resize",
    options: [
      { value: "col-resize", label: "Col Resize" },
      { value: "row-resize", label: "Row Resize" },
      { value: "n-resize", label: "N Resize" },
      { value: "e-resize", label: "E Resize" },
      { value: "s-resize", label: "S Resize" },
      { value: "w-resize", label: "W Resize" },
      { value: "ne-resize", label: "NE Resize" },
      { value: "nw-resize", label: "NW Resize" },
      { value: "se-resize", label: "SE Resize" },
      { value: "sw-resize", label: "SW Resize" },
      { value: "ew-resize", label: "EW Resize" },
      { value: "ns-resize", label: "NS Resize" },
      { value: "nesw-resize", label: "NESW Resize" },
      { value: "nwse-resize", label: "NWSE Resize" }
    ]
  }
];

// Every cursor row carries `effects-cursor-option-<css value>`, so a test picks a cursor by the
// value it writes rather than by its label copy.
const CURSOR_GROUPS: KuikaSelectboxOptionGroup[] = CURSOR_GROUP_DEFINITIONS.map(
  (group: KuikaSelectboxOptionGroup): KuikaSelectboxOptionGroup => ({
    ...group,
    options: group.options.map((option: KuikaSelectboxOption): KuikaSelectboxOption => ({
      ...option,
      testId: `effects-cursor-option-${String(option.value)}`
    }))
  })
);

export type EffectsBasicSlice = "head" | "tail";

export interface IEffectsBasicComponentProps {
  form: FormInstance<IKuikaStyle>;
  slice: EffectsBasicSlice;
}

interface IEffectsBasicComponentState {
  placeholder: never[];
}

class EffectsBasicComponent extends PureSdComponent<
  IEffectsBasicComponentProps & IWithSDProps,
  IEffectsBasicComponentState
> {
  constructor(props: IEffectsBasicComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  private submitForm = (fieldsValue: IKuikaStyle): void => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  handleOpacityCommit = (value: number): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.opacity = value;
    }
    this.submitForm(fieldsValue);
  };

  handleCursorChange = (value: string | number | (string | number)[] | null): void => {
    const next: string = typeof value === "string" ? value : "";
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.cursor = next;
    }
    this.submitForm(fieldsValue);
  };

  handlePointerEventsChange = (value: string): void => {
    const next: string = (value || "").toLowerCase();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.pointerEvents = next;
    }
    this.submitForm(fieldsValue);
  };

  handleBlendingChange = (value: string | number | (string | number)[] | null): void => {
    const next: string = typeof value === "string" ? value : "";
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.mixBlendMode = next;
    }
    this.submitForm(fieldsValue);
  };

  private hasValue = (field: string): boolean => {
    const value: unknown = this.props.form.getFieldValue(["effects", field]);
    if (value === undefined || value === null || value === "") return false;
    // CSS defaults — selecting these reverts to the inherited/initial value
    // and should not trigger the "modified" indicator.
    if (field === "mixBlendMode" && value === "normal") return false;
    if (field === "pointerEvents" && value === "auto") return false;
    if (field === "cursor" && (value === "auto" || value === "default")) return false;
    return true;
  };

  private isEditingTag = (): boolean => {
    const effectsId: string | null | undefined = this.props.form.getFieldValue(["effects", "effectsId"]);
    return !!(effectsId && effectsId.length > 0 && effectsId !== "-1");
  };

  private resetSectionValues = (fields: string[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.effects) {
      fields.forEach((field: string): void => {
        if (fieldsValue.effects) {
          (fieldsValue.effects as unknown as Record<string, unknown>)[field] = undefined;
        }
      });
      this.submitForm(fieldsValue);
    }
  };

  private getLabelClassName = (field: string): string => {
    const editing: boolean = this.isEditingTag();
    const hasValue: boolean = this.hasValue(field);
    return classNames({
      "kuika-styling-panel__label--has-value": editing && hasValue,
      "kuika-styling-panel__label--changed": !editing && hasValue
    });
  };

  private renderBlending = (): React.ReactNode => (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const currentBlend: string = (this.props.form.getFieldValue(["effects", "mixBlendMode"]) as string) || "";
        return (
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["mixBlendMode"]}
              section="blending"
              hasValue={this.hasValue("mixBlendMode")}
              className={this.getLabelClassName("mixBlendMode")}
              onReset={this.resetSectionValues}
            >
              Blending
            </StyleLabel>
            <KuikaSelectbox
              size="sm"
              options={BLENDING_OPTIONS}
              value={currentBlend || undefined}
              onChange={this.handleBlendingChange}
              placeholder="Normal"
              data-testid="effects-blending-select"
            />
          </div>
        );
      }}
    </Form.Item>
  );

  private renderOpacity = (): React.ReactNode => (
    <Form.Item noStyle shouldUpdate>
      {() => (
        <div className="kuika-styling-panel__form-field-wrapper">
          <StyleLabel
            fieldPaths={["opacity"]}
            section="opacity"
            hasValue={this.hasValue("opacity")}
            className={this.getLabelClassName("opacity")}
            onReset={this.resetSectionValues}
          >
            Opacity
          </StyleLabel>
          <Form.Item
            name={["effects", "opacity"]}
            noStyle
            // Visual-only default: with no opacity set, show the CSS default (1 = fully
            // opaque) instead of an empty/zero slider. getValueProps only maps the
            // DISPLAYED value — the stored field stays undefined until the user drags,
            // so this never writes a default into the styleObj metadata.
            getValueProps={(value: number | null | undefined) => ({
              value: value === undefined || value === null ? 1 : value
            })}
          >
            <KuikaSlider
              min={0}
              max={1}
              step={0.01}
              precision={2}
              showInput
              onAfterChange={this.handleOpacityCommit}
              dataTestId="effects-opacity-slider"
            />
          </Form.Item>
        </div>
      )}
    </Form.Item>
  );

  private renderCursor = (): React.ReactNode => (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const currentCursor: string = (this.props.form.getFieldValue(["effects", "cursor"]) as string) || "";
        return (
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["cursor"]}
              section="cursor"
              hasValue={this.hasValue("cursor")}
              className={this.getLabelClassName("cursor")}
              onReset={this.resetSectionValues}
            >
              Cursor
            </StyleLabel>
            <KuikaSelectbox
              size="sm"
              groups={CURSOR_GROUPS}
              value={currentCursor || undefined}
              onChange={this.handleCursorChange}
              placeholder="Default"
              searchable
              data-testid="effects-cursor-select"
            />
          </div>
        );
      }}
    </Form.Item>
  );

  private renderPointer = (): React.ReactNode => (
    <Form.Item noStyle shouldUpdate>
      {() => {
        // Visual-only default: fall back to the CSS default "auto" so that tab is
        // shown selected when nothing is set. Used only for the displayed `value`
        // below; the modified indicator still reads the raw store via hasValue.
        const currentPointer: string =
          (this.props.form.getFieldValue(["effects", "pointerEvents"]) as string) || "auto";
        return (
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["pointerEvents"]}
              section="pointer"
              hasValue={this.hasValue("pointerEvents")}
              className={this.getLabelClassName("pointerEvents")}
              onReset={this.resetSectionValues}
            >
              Pointer
            </StyleLabel>
            <KuikaTabs
              options={POINTER_EVENTS_OPTIONS}
              value={currentPointer}
              onChange={this.handlePointerEventsChange}
              dataTestId="effects-pointer-events-tabs"
            />
          </div>
        );
      }}
    </Form.Item>
  );

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    if (this.props.slice === "head") {
      return (
        <>
          {this.renderBlending()}
          {this.renderOpacity()}
        </>
      );
    }

    return (
      <>
        {this.renderCursor()}
        {this.renderPointer()}
      </>
    );
  }
}

export default withSD<IEffectsBasicComponentProps>(EffectsBasicComponent);
